import { locationAdapter } from '@/adapters/locations';
import { ticketAdapter } from '@/adapters/ticket';

const routeGroupAdapter = (data, isRouteSoldOut, tickets) => {
  const { count } = data;

  const groupTickets =
    tickets?.filter((ticket) => ticket.groupId === data.id) || [];
  const isSoldOut =
    isRouteSoldOut || groupTickets.every((ticket) => ticket.isSoldOut);

  return {
    id: data.id,
    name: data.name,
    isSoldOut,
    count:
      !isSoldOut && count?.count > 0
        ? {
            key: count.key,
            color: count.color,
            count: count.count,
          }
        : null,
  };
};

export const routeAdapter = (data) => {
  const { train, carrier, count } = data;

  const tickets = data.tickets?.map(ticketAdapter);
  const groups = data.groups ? data.groups : [];

  return {
    id: data.id,

    departureLocation: locationAdapter(data.departure_location),
    departsAt: data.departs_at,

    arrivalLocation: locationAdapter(data.arrival_location),
    arrivesAt: data.arrives_at,

    train: train && {
      id: train.id,
      code: train.code,
      type: train.type,
      isHighSpeed: train.is_highspeed,
      isIntercity: train.is_intercity,
      isNight: train.is_night,
    },

    distance: data.distance,
    duration: data.duration,

    isSoldOut: data.is_sold_out,
    count: count && {
      key: count.key,
      color: count.color,
      count: count.count,
    },
    onlyOneGroup: data.only_one_group,

    carrier: carrier && {
      id: carrier.id,
      name: carrier.name,
      image: carrier.image,
    },

    groups: groups?.map((group) =>
      routeGroupAdapter(group, data.is_sold_out, tickets),
    ),
    tickets,
  };
};

export const routesAdapter = (items) => {
  return items.map(routeAdapter);
};

export const routeDatePreviewAdapter = (data) => {
  return {
    date: data.date,
    price: data.price, // currencies object { cad: 25.15 }
  };
};

export const routeDatePreviewsAdapter = (items) => {
  return items.map(routeDatePreviewAdapter);
};

export const routeAlertAdapter = (data) => {
  return {
    id: data.id,
    content: data.content,
    type: data.type,
  };
};

export const routeAlertsAdapter = (items) => {
  if (!items) return [];
  return items.map(routeAlertAdapter);
};

// RN source: src/helpers/getCheapestRoutes.js
export const routesWithLabelsAdapter = (routes) => {
  let lowerPrice = 0;
  let lowerTicketIds = [];
  let fastestTime = 0;
  let fastestTimeIds = [];
  let hasHigherPrice = false;
  let hasSlowerTime = false;
  let isDurationsSame = true;

  let previousDuration = null;

  routes.forEach((route, index) => {
    if (route.isSoldOut) return;

    let price = route.tickets?.reduce((min, ticket) => {
      return ticket.totalPrice?.['usd'] < min ? ticket.totalPrice['usd'] : min;
    }, Infinity);

    let duration = route.duration;

    if (previousDuration !== null && previousDuration !== duration) {
      isDurationsSame = false;
    }
    previousDuration = duration;

    if (duration < fastestTime || index === 0) {
      fastestTime = duration;
      fastestTimeIds = [route.id];
      if (index !== 0) hasSlowerTime = true;
    } else if (duration === fastestTime) {
      fastestTimeIds.push(route.id);
    } else if (duration > fastestTime) {
      hasSlowerTime = true;
    }

    if (price < lowerPrice || index === 0) {
      lowerPrice = price;
      lowerTicketIds = [route.id];
      if (index !== 0) hasHigherPrice = true;
    } else if (price === lowerPrice) {
      lowerTicketIds.push(route.id);
    } else if (price > lowerPrice) {
      hasHigherPrice = true;
    }
  });

  return routes.map((route) => {
    return {
      ...route,
      isCheapest: hasHigherPrice && lowerTicketIds.includes(route.id),
      isFastest: hasSlowerTime && fastestTimeIds.includes(route.id),
      hasHigherPrice,
      hasSlowerTime,
      isDurationsSame,
    };
  });
};
